import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FaKey } from 'react-icons/fa';
import { Spinner } from 'react-bootstrap';
import { YButton } from '../../../ui/YButton';
import { YInput } from '../../../ui/YInput';
import { useToast } from '../../../components/ui/ToastProvider';
import { joinClubByCode } from '../services/joinByCodeService';
import { isProRequiredError } from '../../pro/entitlements';
import { ProPanel } from '../../pro/ProPanel';
import {
  joinByCodeErrorMessage,
  joinByCodeSuccessMessage,
  joinByCodeToastContent,
  normalizeInviteCodeInput,
} from '../utils/joinByCodeMessages';
import { invalidateCachedClubsList } from '../utils/clubsCache';
import './JoinByCodeWidget.css';

export function JoinByCodeWidget({
  variant = 'banner',
  className = '',
  onSuccess,
  title = 'Have an invite code?',
  description = 'Received a code from a club president? Enter it to join right away.',
}) {
  const navigate = useNavigate();
  const { show: showToast } = useToast();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [proRequired, setProRequired] = useState(false);

  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault();
      const normalized = normalizeInviteCodeInput(code);
      if (!normalized) {
        setError('Enter the invite code from your club president.');
        setSuccess(null);
        return;
      }
      try {
        setSubmitting(true);
        setError('');
        const data = await joinClubByCode(normalized);
        invalidateCachedClubsList();
        setSuccess(data);
        const toast = joinByCodeToastContent(data);
        try {
          showToast(toast);
        } catch (_) { /* no-op */ }
        onSuccess?.(data);
        if (data?.redirect) {
          navigate(data.redirect);
        }
      } catch (err) {
        setSuccess(null);
        if (isProRequiredError(err, 'clubs')) {
          setProRequired(true);
          return;
        }
        setError(joinByCodeErrorMessage(err));
      } finally {
        setSubmitting(false);
      }
    },
    [code, navigate, onSuccess, showToast]
  );

  const rootClass = `join-by-code join-by-code--${variant}${className ? ` ${className}` : ''}`;
  const isField = variant === 'field';

  if (proRequired) {
    return <ProPanel onDismiss={() => setProRequired(false)} />;
  }

  return (
    <div className={rootClass} data-testid="join-by-code-widget">
      {!isField ? <div className="join-by-code__accent" aria-hidden /> : null}
      <div className="join-by-code__inner">
        {isField ? (
          <div className="join-by-code__field-copy">
            <div className="join-by-code__kicker">{title}</div>
            <div className="join-by-code__headline">{description}</div>
          </div>
        ) : (
          <div className="join-by-code__header">
            <span className="join-by-code__icon" aria-hidden>
              <FaKey />
            </span>
            <div className="join-by-code__copy">
              <div className="join-by-code__title-row">
                <h3 className="join-by-code__title">{title}</h3>
                <span className="join-by-code__badge">Join instantly</span>
              </div>
              <p className="join-by-code__desc">{description}</p>
            </div>
          </div>
        )}

        <form className="join-by-code__form" onSubmit={handleSubmit}>
          <YInput
            className="join-by-code__input y-input--mono"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              if (error) setError('');
              if (success) setSuccess(null);
            }}
            placeholder={isField ? 'CODE' : 'e.g. WTR-2847'}
            aria-label="Invite code"
            autoComplete="off"
            spellCheck={false}
            disabled={submitting}
          />
          <YButton type="submit" className="join-by-code__submit" disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" aria-hidden /> : 'JOIN'}
          </YButton>
        </form>

        {success ? (
          <div
            className={`join-by-code__alert join-by-code__alert--${success.status === 'already_member' ? 'info' : 'success'}`}
            role="status"
          >
            <strong>{success.status === 'already_member' ? 'Already a member' : success.club_name}</strong>
            <span>{joinByCodeSuccessMessage(success)}</span>
          </div>
        ) : null}

        {error ? (
          <div className="join-by-code__alert join-by-code__alert--error" role="alert">
            <strong>Couldn’t join</strong>
            <span>{error}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

JoinByCodeWidget.propTypes = {
  variant: PropTypes.oneOf(['banner', 'compact', 'field']),
  className: PropTypes.string,
  onSuccess: PropTypes.func,
  title: PropTypes.string,
  description: PropTypes.string,
};

export default JoinByCodeWidget;
